/**
 * Visualization Overlays for Heat Pump Simulation
 * Displays radiator ratings and heat flux visualizations
 */

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ROOM_WIDTH, ROOM_HEIGHT } from "./constants";

/**
 * Radiator Rating Overlay
 * Shows heat output and temperature information
 */
export const RadiatorRatingOverlay = ({
  heatOutput,
  supplyTemperature,
  returnTemperature,
  radiatorCount,
}) => {
  const rating = heatOutput / 5000;
  const startX = ROOM_WIDTH * 0.125 + 3.5 * radiatorCount;

  return (
    <svg>
      <rect width={ROOM_WIDTH + 5} height={ROOM_HEIGHT} fill="rgba(255,255,255,0.6)" />

      {/* Heat output label */}
      <text x={30} y={30} fill="green" fontSize={14}>
        {(heatOutput / 1000).toFixed(2)} kW
      </text>

      {/* Heat arrow indicator */}
      <path
        fill={rating ? "green" : "gray"}
        d={`M${startX} ${ROOM_HEIGHT * 0.8325} 
            L${startX + 5} ${ROOM_HEIGHT * 0.8325} 
            L${startX + 5} ${ROOM_HEIGHT * 0.8325 - rating * 40} 
            L${startX + 10} ${ROOM_HEIGHT * 0.8325 - rating * 40} 
            L${startX} ${ROOM_HEIGHT * 0.8325 - 10 - rating * 40} 
            L${startX - 10} ${ROOM_HEIGHT * 0.8325 - rating * 40} 
            L${startX - 5} ${ROOM_HEIGHT * 0.8325 - rating * 40} 
            L${startX - 5} ${ROOM_HEIGHT * 0.8325}z`}
      />

      {/* Temperature display box */}
      <rect
        x={ROOM_WIDTH * 0.1}
        y={ROOM_HEIGHT * 0.8325 + 2}
        width={19 + 7 * radiatorCount}
        height={50}
        fill="rgba(255,255,255,1)"
        stroke="rgba(0,0,0,0.3)"
        strokeWidth={1}
      />
      <text x={ROOM_WIDTH * 0.1 + 5} y={ROOM_HEIGHT * 0.885 + 20} fill="rgba(0, 130, 255, 1)" fontSize={12}>
        {returnTemperature.toFixed(1)}°C
      </text>
      <text x={ROOM_WIDTH * 0.1 + 5} y={ROOM_HEIGHT * 0.885} fill="red" fontSize={12}>
        {supplyTemperature}°C
      </text>
    </svg>
  );
};

RadiatorRatingOverlay.propTypes = {
  heatOutput: PropTypes.number.isRequired,
  supplyTemperature: PropTypes.number.isRequired,
  returnTemperature: PropTypes.number.isRequired,
  radiatorCount: PropTypes.number.isRequired,
};

/**
 * Heat arrow path generator
 */
const generateHeatArrow = (direction, position, powerWatts) => {
  const { x, y } = position;
  const arrowLength = powerWatts * 0.2; // 100W = 20px scale

  switch (direction) {
    case "west":
      return `M${x + 10} ${y - 5} L${x + 10} ${y + 5} L${x - arrowLength} ${y + 5} 
              L${x - arrowLength} ${y + 10} L${x - arrowLength - 10} ${y} 
              L${x - arrowLength} ${y - 10} L${x - arrowLength} ${y - 5} L${x + 10} ${y - 5} z`;

    case "east":
      return `M${x - 10} ${y - 5} L${x - 10} ${y + 5} L${x + arrowLength} ${y + 5} 
              L${x + arrowLength} ${y + 10} L${x + arrowLength + 10} ${y} 
              L${x + arrowLength} ${y - 10} L${x + arrowLength} ${y - 5} L${x - 10} ${y - 5} z`;

    case "north":
      return `M${x - 5} ${y + 10} L${x + 5} ${y + 10} L${x + 5} ${y - arrowLength} 
              L${x + 10} ${y - arrowLength} L${x} ${y - arrowLength - 10} 
              L${x - 10} ${y - arrowLength} L${x - 5} ${y - arrowLength} L${x - 5} ${y + 10} z`;

    case "south":
      return `M${x - 5} ${y - 10} L${x + 5} ${y - 10} L${x + 5} ${y + arrowLength} 
              L${x + 10} ${y + arrowLength} L${x} ${y + arrowLength + 10} 
              L${x - 10} ${y + arrowLength} L${x - 5} ${y + arrowLength} L${x - 5} ${y - 10} z`;

    default:
      return "";
  }
};

/**
 * Heat Flux Overlay
 * Shows heat flows in all directions
 */
export const HeatFluxOverlay = ({
  heatOutput,
  heatLoss,
  ventilationLoss,
  heatLossBreakdown,
  roomTemperature,
  temperatureChange,
}) => {
  const { t } = useTranslation();

  const totalLoss = heatLoss + ventilationLoss;
  const netHeat = heatOutput - totalLoss;
  const padding = 150;

  // Position calculations
  const boxOffsetX = -5;
  const boxOffsetY = 8;
  const centerY = padding + ROOM_HEIGHT * 0.5;

  return (
    <svg>
      {/* Semi-transparent background */}
      <rect x={padding} y={padding} width={ROOM_WIDTH - 2} height={ROOM_HEIGHT - 2} fill="rgba(255, 255, 255, 0.6)" />

      {/* Heat balance information box */}
      <rect x={padding + ROOM_WIDTH - 110 + boxOffsetX} y={padding + 5 + boxOffsetY} width={99} height={110} fill="white" stroke="none" />

      <text x={padding + ROOM_WIDTH - 115 + boxOffsetX} y={padding + 15 + boxOffsetY} fontSize={10} fill="gray">
        {t('heatPumpSimulation.overlays.radiator')}
      </text>
      <text x={padding + ROOM_WIDTH - 50 + boxOffsetX} y={padding + 15 + boxOffsetY} fontSize={10} fill="green">
        +{(heatOutput / 1000).toFixed(2)}
      </text>

      <text x={padding + ROOM_WIDTH - 115 + boxOffsetX} y={padding + 35 + boxOffsetY} fontSize={10} fill="gray">
        {t('heatPumpSimulation.overlays.envelopeLoss')}
      </text>
      <text x={padding + ROOM_WIDTH - 50 + boxOffsetX} y={padding + 35 + boxOffsetY} fontSize={10} fill={heatLoss > 0 ? "red" : "green"}>
        {heatLoss > 0 ? "-" : "+"}
        {(Math.abs(heatLoss) / 1000).toFixed(2)}
      </text>

      <text x={padding + ROOM_WIDTH - 115 + boxOffsetX} y={padding + 55 + boxOffsetY} fontSize={10} fill="gray">
        {t('heatPumpSimulation.overlays.ventilationLoss')}
      </text>
      <text x={padding + ROOM_WIDTH - 50 + boxOffsetX} y={padding + 55 + boxOffsetY} fontSize={10} fill={ventilationLoss > 0 ? "red" : "green"}>
        {ventilationLoss > 0 ? "-" : "+"}
        {(Math.abs(ventilationLoss) / 1000).toFixed(2)}
      </text>

      <line x1={padding + ROOM_WIDTH - 115 + boxOffsetX} x2={padding + ROOM_WIDTH - 5 + boxOffsetX} y1={padding + 65 + boxOffsetY} y2={padding + 65 + boxOffsetY} stroke="gray" strokeWidth={1} />

      <text x={padding + ROOM_WIDTH - 115 + boxOffsetX} y={padding + 80 + boxOffsetY} fontSize={10} fill="gray">
        {t('heatPumpSimulation.overlays.total')}
      </text>
      <text x={padding + ROOM_WIDTH - 50 + boxOffsetX} y={padding + 80 + boxOffsetY} fontSize={10} fill={netHeat > 0 ? "green" : "red"}>
        {netHeat > 0 ? "+" : "-"}
        {(Math.abs(netHeat) / 1000).toFixed(2)} kW
      </text>

      <text x={padding + ROOM_WIDTH - 115 + boxOffsetX} y={padding + 100 + boxOffsetY} fontSize={10} fill="gray">
        {t('heatPumpSimulation.overlays.temperature')}
      </text>
      <text x={padding + ROOM_WIDTH - 50 + boxOffsetX} y={padding + 100 + boxOffsetY} fontSize={10} fill="blue">
        {roomTemperature.toFixed(1)}
      </text>
      <text x={padding + ROOM_WIDTH - 50 + boxOffsetX} y={padding + 110 + boxOffsetY} fontSize={8} fill={temperatureChange >= 0 ? "green" : "red"}>
        {temperatureChange >= 0 ? "+" : ""}
        {temperatureChange.toFixed(4)}
      </text>
      <text x={padding + ROOM_WIDTH - 30 + boxOffsetX} y={padding + 100 + boxOffsetY} fontSize={10} fill="blue">
        °C
      </text>

      {/* Heat loss arrows */}
      {heatLossBreakdown && (
        <>
          {/* West wall loss */}
          {Math.abs(heatLossBreakdown.west) > 0 && (
            <path
              fill={heatLossBreakdown.west > 0 ? "#ff4d4f" : "#52c41a"}
              d={generateHeatArrow(
                heatLossBreakdown.west > 0 ? "west" : "east",
                { x: padding, y: centerY },
                Math.abs(heatLossBreakdown.west)
              )}
            />
          )}

          {/* East wall loss */}
          {Math.abs(heatLossBreakdown.east) > 0 && (
            <path
              fill={heatLossBreakdown.east > 0 ? "#ff4d4f" : "#52c41a"}
              d={generateHeatArrow(
                heatLossBreakdown.east > 0 ? "east" : "west",
                { x: padding + ROOM_WIDTH, y: centerY },
                Math.abs(heatLossBreakdown.east)
              )}
            />
          )}

          {/* North wall loss */}
          {Math.abs(heatLossBreakdown.north) > 0 && (
            <path
              fill={heatLossBreakdown.north > 0 ? "#ff4d4f" : "#52c41a"}
              d={generateHeatArrow(
                heatLossBreakdown.north > 0 ? "north" : "south",
                { x: padding + ROOM_WIDTH * 0.5, y: padding },
                Math.abs(heatLossBreakdown.north)
              )}
            />
          )}
        </>
      )}
    </svg>
  );
};

HeatFluxOverlay.propTypes = {
  heatOutput: PropTypes.number.isRequired,
  heatLoss: PropTypes.number.isRequired,
  ventilationLoss: PropTypes.number.isRequired,
  heatLossBreakdown: PropTypes.shape({
    west: PropTypes.number,
    east: PropTypes.number,
    north: PropTypes.number,
    south: PropTypes.number,
  }),
  roomTemperature: PropTypes.number.isRequired,
  temperatureChange: PropTypes.number.isRequired,
  baseX: PropTypes.number,
  baseY: PropTypes.number,
};
